/**
 * Booking money totals — the single place the tax split is computed.
 *
 * Every path that recalculates a booking's totals (create, date change, item
 * change, delivery price change, discount applied or removed) has to agree on
 * how `subtotal`, `total_tax` and `total_amount` relate, or a booking's stored
 * tax drifts away from its stored total depending on which button was pressed
 * last.
 *
 * That is exactly what happened: the five edit paths each carried their own
 * copy of the arithmetic, and every copy handled only the tax-EXCLUSIVE case:
 *
 *     let newTotalTax = 0;
 *     if (!booking.tax_included && booking.tax_rate) { ... }
 *
 * For a tax-inclusive merchant that leaves `total_tax` at 0 and collapses
 * `total_amount` to `subtotal + delivery - discount`. Bookings were created
 * with the correct split and then had it wiped by the first edit — including
 * edits that moved no money at all, such as adding a €0 item. Roughly 647
 * bookings across 14 merchants and four tax jurisdictions lost their VAT this
 * way, about €10k of tax on €63.9k of gross.
 *
 * Tax-inclusive vs tax-exclusive:
 *   inclusive — the advertised price already contains the tax. The total does
 *               not move; the tax is the portion inside it, base × r/(1+r).
 *   exclusive — the tax is added on top, so the total grows by base × r.
 *
 * `taxIncluded` is compared against `true` rather than tested for falsiness on
 * purpose. `bookings.tax_included` is nullable, and the edit paths have always
 * treated NULL as exclusive (`!null` is true). Preserving that keeps this
 * change strictly additive: only rows with `tax_included = true` behave
 * differently, and for those only `total_tax` changes — `total_amount` is
 * `base` before and after, so no booking total moves and no customer is
 * charged differently.
 */

export interface BookingTotalsInput {
  /** Sum of the booking's item totals. */
  subtotal: number;
  deliveryPrice?: number | null;
  totalDiscount?: number | null;
  /** Fractional rate, e.g. 0.19 for 19%. */
  taxRate?: number | null;
  /** TRUE = prices already contain the tax. NULL is treated as exclusive. */
  taxIncluded?: boolean | null;
}

export interface BookingTotals {
  subtotal: number;
  totalTax: number;
  totalAmount: number;
}

/** Currency values are stored to 2dp; round at every boundary so repeated
 *  edits cannot accumulate floating-point dust in a money column. */
const round2 = (value: number): number => Math.round(value * 100) / 100;

export function computeBookingTotals({
  subtotal,
  deliveryPrice,
  totalDiscount,
  taxRate,
  taxIncluded,
}: BookingTotalsInput): BookingTotals {
  const roundedSubtotal = round2(subtotal || 0);
  const base = round2(roundedSubtotal + (deliveryPrice || 0) - (totalDiscount || 0));
  const rate = taxRate || 0;

  if (rate <= 0) {
    return { subtotal: roundedSubtotal, totalTax: 0, totalAmount: base };
  }

  if (taxIncluded === true) {
    // The tax is already inside `base`; extracting it must not change the total.
    return {
      subtotal: roundedSubtotal,
      totalTax: round2(base * (rate / (1 + rate))),
      totalAmount: base,
    };
  }

  const totalTax = round2(base * rate);
  return { subtotal: roundedSubtotal, totalTax, totalAmount: round2(base + totalTax) };
}
